// The chip overlay's mapping: a replay question, as the server read it off the
// SessionReplayQuestion row, into the shape `ConsensusActivityRender` draws.
//
// The row is the source, not the question bank: it holds the prompt, the
// options and the answer key as they were asked, which for an edited or
// archived question is not what the bank holds today.

import { REVIEW_STATE } from "constants/reviewStates";
import { EM_DASH, sessionDateParts } from "./studentReportTable";

const asText = (value) => (value === null || value === undefined ? null : String(value));

/**
 * Where the recorded correct answer sits among the options, or `null`.
 *
 * The replay row stores the correct answer as a value and the renderer wants
 * an index. When the value cannot be located the overlay marks nothing
 * correct rather than guessing, since a guess looks exactly like a right one.
 */
export const solutionIndexOf = (answerOptions, correctAnswer) => {
  const target = asText(correctAnswer);
  if (target === null || !Array.isArray(answerOptions)) return null;
  const index = answerOptions.findIndex((option) => asText(option) === target);
  return index === -1 ? null : index;
};

/**
 * The question itself, in the renderer's own vocabulary.
 *
 * `answer_options` is NULL, never `[]`, when the question has none. The
 * renderer branches on the field's truthiness, and `[]` would draw an open
 * response as a multiple-choice question with no options.
 */
export const overlayChallenge = (question = {}) => ({
  prompt: question.question_text ?? "",
  answer_options: question.answer_options?.length ? question.answer_options : null,
  solution_index: solutionIndexOf(question.answer_options, question.correct_answer),
  solution: question.correct_answer ?? null,
  prompt_image_url: question.image_url ?? null,
  prompt_image_latex: question.prompt_image_latex ?? null,
  render_type: "fraction_legacy",
  standards: Array.isArray(question.standards) ? question.standards : [],
});

/**
 * The 0-BASED position the server resolved for this student, or `null`.
 *
 * The position is resolved on the server with the same logic the compiler
 * grades with; the browser converts nothing.
 *
 * The absent check is `=== null` because `0`, the first option, is falsy.
 */
export const studentAnswerIndex = (question = {}) => {
  const position = question.student_answer_index;
  return position === null || position === undefined ? null : position;
};

/**
 * True when the option the position names and the row's own verdict cannot both
 * be believed.
 *
 * On rows compiled before 2026-08-27 the verdict compared the stored index
 * against the option's text, so on word options it is false for every attempt.
 * The overlay does not choose between them: it marks no option as the
 * student's, keeps the verdict, and says the two could not be reconciled.
 *
 * When the correct answer cannot be located there is nothing to contradict.
 */
export const isUnreconciled = (question = {}) => {
  const position = studentAnswerIndex(question);
  if (position === null) return false;
  const solution = solutionIndexOf(question.answer_options, question.correct_answer);
  if (solution === null) return false;
  return (position === solution) !== Boolean(question.student_correct);
};

/**
 * This student's answer, in the map the renderer reads — and NOTHING ELSE.
 *
 * The server already filtered the group's responses down to this student, and
 * this builds a map of exactly one entry so the browser cannot draw a
 * teammate. `null` when there is no recorded answer.
 */
export const overlayStudentResponses = (question = {}, { studentName } = {}) => {
  const answer = asText(question.student_answer);
  if (answer === null) return null;
  return {
    self: {
      oko_user_id: "self",
      display_name: studentName || null,
      option_index: isUnreconciled(question) ? null : studentAnswerIndex(question),
      solution: answer,
      is_correct: Boolean(question.student_correct),
    },
  };
};

/**
 * One entry of the modal's explicit list.
 *
 * An entry always exists, whatever happened to its question: dropping an
 * unreadable one would shift every later entry onto a different question.
 *
 * `question === null` means "not fetched yet"; a question whose `question_text`
 * is null means "the row is there and its stored data could not be read".
 */
export const overlayEntry = ({ label, question, studentName } = {}) => {
  const base = {
    id: label,
    overlay_label: label,
    overlay_pending: question === null || question === undefined,
    overlay_unavailable: Boolean(question) && !question.question_text,
    overlay_correct: question ? Boolean(question.student_correct) : null,
    overlay_unreconciled: Boolean(question) && isUnreconciled(question),
    overlay_answer: question ? asText(question.student_answer) : null,
    overlay_student_name: studentName || null,
    // The info band follows the shown question, since chips of one standard
    // span sessions. Until the question arrives both read an em dash.
    overlay_date: question ? overlayDate(question.session_started_at) : EM_DASH,
    overlay_group_code: question ? question.group_code ?? null : EM_DASH,
    overlay_option_states: question ? reviewOptionStates(question) : null,
    overlay_answer_state: question ? reviewAnswerState(question) : null,
  };
  if (!question) return { ...base, prompt: "", answer_options: null, solution_index: null };
  return {
    ...base,
    ...overlayChallenge(question),
    student_responses: overlayStudentResponses(question, { studentName }),
  };
};

export { REVIEW_STATE };

/**
 * One review state per option, or `null` for a question with no options.
 *
 * Decided here, from the position the server resolved, so the renderer only
 * paints what it is handed.
 */
export const reviewOptionStates = (question = {}) => {
  if (!question.answer_options?.length) return null;
  const solution = solutionIndexOf(question.answer_options, question.correct_answer);
  // An answer that cannot be reconciled with its verdict is marked as nobody's.
  const chosen = isUnreconciled(question) ? null : studentAnswerIndex(question);
  return question.answer_options.map((_, index) => {
    if (index === solution) return REVIEW_STATE.CORRECT;
    if (index === chosen) return REVIEW_STATE.HERS_INCORRECT;
    return REVIEW_STATE.OTHER;
  });
};

/**
 * The state of the student's own answer box on an open response, or `null`
 * when none was recorded. The verdict is the row's own, the one the chip
 * showed.
 */
export const reviewAnswerState = (question = {}) => {
  if (asText(question.student_answer) === null) return null;
  return question.student_correct ? REVIEW_STATE.CORRECT : REVIEW_STATE.HERS_INCORRECT;
};

/**
 * The info band's Date: a short weekday, then the Session table's own date and
 * time joined by " • ". Composed from `sessionDateParts` so the overlay and the
 * Session table cannot disagree.
 */
export const overlayDate = (startedAt) => {
  const { date, time } = sessionDateParts(startedAt);
  if (date === EM_DASH) return EM_DASH;
  const weekday = new Date(startedAt).toLocaleDateString("en-US", { weekday: "short" });
  return `${weekday}, ${date} \u2022 ${time}`;
};
